import React, { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import Picture from './Picture';
import { getAdminBadge } from '../utils/adminBadge';
import { groupVisit } from '../utils/visitGroups';

const Thumbnail = ({ picture, caption, highlighted, isAdmin, onOpen, highlightRef, t }) => (
  <button
    type="button"
    ref={highlighted ? highlightRef : undefined}
    onClick={() => onOpen(picture)}
    className={[
      'group relative block overflow-hidden rounded-xl bg-gray-100 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:bg-gray-800',
      highlighted ? 'ring-4 ring-blue-500 ring-offset-2 ring-offset-white dark:ring-offset-gray-900' : 'hover:opacity-90'
    ].join(' ')}
  >
    <div className="aspect-[4/3] w-full">
      <Picture pictureurl={picture.pictureurl} className="h-full w-full object-cover" />
    </div>
    {highlighted && (
      <span className="absolute left-2 top-2 rounded-full bg-blue-600 px-2 py-0.5 text-[0.65rem] font-semibold text-white shadow">
        {t('Highlighted photo')}
      </span>
    )}
    <span className="absolute right-2 top-2 flex items-center gap-1">
      {isAdmin && picture.admin_only && <span className="rounded-full bg-white/90 p-0.5 shadow">{getAdminBadge()}</span>}
      {picture.favorite && (
        <svg className="h-5 w-5 fill-current text-red-500 drop-shadow" viewBox="0 0 24 24">
          <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      )}
    </span>
    {caption && (
      <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/75 to-transparent px-2 pb-1.5 pt-5 text-xs font-medium text-white" title={caption}>
        {caption}
      </span>
    )}
  </button>
);

const Grid = ({ children }) => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 2xl:grid-cols-8">{children}</div>
);

/**
 * Every picture of one visit, grouped by question then by item, with one picture highlighted.
 *
 * Presentational: the visit is loaded by `useVisit` and kept by the gallery, so a favourite toggled
 * from the photo modal shows here too.
 */
function VisitModal({ visit, highlightId, isAdmin, onOpenPicture, onClose }) {
  const { t } = useTranslation();
  const highlightRef = useRef(null);
  const sectionRefs = useRef([]);
  const [copyStatus, setCopyStatus] = useState(null);
  const grouped = useMemo(() => groupVisit(visit.pictures), [visit.pictures]);
  const first = visit.pictures[0];
  const last = visit.pictures[visit.pictures.length - 1];
  const highlighted = Number(highlightId);

  useEffect(() => {
    if (visit.status === 'ready') highlightRef.current?.scrollIntoView({ block: 'center' });
  }, [visit.status, highlighted]);

  useEffect(() => {
    if (!copyStatus) return undefined;
    const timeout = window.setTimeout(() => setCopyStatus(null), 2200);
    return () => window.clearTimeout(timeout);
  }, [copyStatus]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/gallery/visit/${highlightId}`);
      setCopyStatus(t('Share link copied'));
    } catch (error) {
      setCopyStatus(t('Unable to copy share link'));
    }
  };

  const captionOf = (picture, withItem) => [
    withItem ? picture.item : null,
    grouped.timesVary && picture.createdat ? dayjs(picture.createdat).format('LT') : null,
  ].filter(Boolean).join(' · ');

  const when = first && (grouped.timesVary
    ? `${dayjs(first.createdat).format('L LT')} – ${dayjs(last.createdat).format('LT')}`
    : dayjs(first.createdat).format('L'));

  const thumbnail = (picture, withItem) => (
    <Thumbnail
      key={picture.id}
      picture={picture}
      caption={captionOf(picture, withItem)}
      highlighted={picture.id === highlighted}
      isAdmin={isAdmin}
      onOpen={onOpenPicture}
      highlightRef={highlightRef}
      t={t}
    />
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/90 p-3 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative flex h-[94vh] w-[96vw] max-w-[110rem] flex-col overflow-hidden rounded-3xl bg-white shadow-2xl dark:bg-gray-900"
        onClick={event => event.stopPropagation()}
      >
        <header className="border-b border-gray-200 px-6 pb-4 pt-5 dark:border-gray-800">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">{t('Visit')}</p>
              <h2 className="mt-1 truncate text-xl font-semibold text-gray-900 dark:text-white">{first?.shopname ?? '…'}</h2>
              {first && (
                <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-sm text-gray-500 dark:text-gray-400">
                  <span>{when}</span>
                  <span aria-hidden="true">·</span>
                  <span>{first.projectname}</span>
                  {first.visittype && (<><span aria-hidden="true">·</span><span>{first.visittype}</span></>)}
                  {isAdmin && first.username && (<><span aria-hidden="true">·</span><span className="inline-flex items-center">{first.username}{getAdminBadge()}</span></>)}
                  {first.source && (<><span aria-hidden="true">·</span><span>{first.source}</span></>)}
                  <span aria-hidden="true">·</span>
                  <span className="font-medium text-gray-700 dark:text-gray-200">
                    {t('visitPhotos', { count: visit.pictures.length })} · {t('visitQuestions', { count: grouped.questions })}
                  </span>
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {copyStatus && <span className="text-xs text-gray-500 dark:text-gray-400">{copyStatus}</span>}
              <button
                type="button"
                onClick={handleCopyLink}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 14.25l4.5-4.5" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12.75 7.25l1.05-1.05a3.25 3.25 0 014.6 4.6l-2.1 2.1a3.25 3.25 0 01-4.6 0" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 16.75l-1.05 1.05a3.25 3.25 0 01-4.6-4.6l2.1-2.1a3.25 3.25 0 014.6 0" />
                </svg>
                {t('Copy visit link')}
              </button>
              <button
                type="button"
                title={t('Close')}
                onClick={onClose}
                className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {grouped.questions > 1 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {grouped.sections.map((section, index) => (
                <button
                  key={section.question || index}
                  type="button"
                  onClick={() => sectionRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700 transition hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  {section.question || t('Question')} <span className="text-gray-400">{section.pictures.length}</span>
                </button>
              ))}
            </div>
          )}
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {visit.status === 'loading' && (
            <Grid>
              {Array.from({ length: 12 }, (_, index) => (
                <div key={index} className="aspect-[4/3] animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
              ))}
            </Grid>
          )}

          {visit.status === 'not_found' && (
            <div className="mx-auto mt-16 max-w-md rounded-2xl border border-dashed border-gray-300 p-8 text-center dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('Visit not found or not accessible')}</p>
              <button type="button" onClick={onClose} className="mt-4 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200">{t('Close')}</button>
            </div>
          )}

          {visit.status === 'error' && (
            <div className="mx-auto mt-16 max-w-md rounded-2xl border border-dashed border-red-300 p-8 text-center dark:border-red-900">
              <p className="text-sm text-red-600 dark:text-red-300">{t('Unable to load the visit')}</p>
              <button type="button" onClick={visit.reload} className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700">{t('Retry')}</button>
            </div>
          )}

          {visit.status === 'ready' && (
            <div className="space-y-8">
              {visit.pictures.length === 1 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('This visit has a single photo')}</p>
              )}
              {visit.truncated && (
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                  {t('Only the first {{count}} photos are shown', { count: visit.pictures.length })}
                </p>
              )}

              {grouped.sections.map((section, index) => (
                <section key={section.question || index} ref={element => { sectionRefs.current[index] = element; }} className="scroll-mt-4">
                  <h3 className="mb-3 flex items-baseline gap-2 text-base font-semibold text-gray-900 dark:text-white">
                    {section.question || t('Question')}
                    <span className="text-sm font-normal text-gray-400">{t('visitPhotos', { count: section.pictures.length })}</span>
                  </h3>

                  {section.items ? (
                    <div className="space-y-4">
                      {section.items.map(group => (
                        <div key={group.item || '__none__'}>
                          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400">
                            {group.item || t('No item')} <span className="font-normal normal-case tracking-normal text-gray-400">· {group.pictures.length}</span>
                          </p>
                          <Grid>{group.pictures.map(picture => thumbnail(picture, false))}</Grid>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Grid>{section.pictures.map(picture => thumbnail(picture, true))}</Grid>
                  )}
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VisitModal;
